/**
 * Timestamp parsing and formatting utilities
 * Centralizes all timestamp handling to ensure consistent China timezone (UTC+8) handling
 *
 * IMPORTANT: This app ALWAYS uses China timezone (Asia/Shanghai, UTC+8) for ALL date/time operations.
 * - Database stores UTC timestamps
 * - All display and business logic uses China timezone
 * - No user timezone preferences - everything is China time
 */

const CHINA_TIMEZONE = 'Asia/Shanghai';

// One Intl.DateTimeFormat per (locale, options), reused: toLocaleString with a
// timeZone builds a new formatter on every call, and the teacher page runs
// formatDateYMD over every assignment of a class (2,484 for 新一11班) several
// times per load. Mirrored in backend/utils/chinaTime.js.
const chinaFormatters = new Map();
const MAX_FORMATTERS = 100;
const DATE_FIELDS = ['weekday', 'year', 'month', 'day', 'dateStyle'];
const ANY_FIELDS = [...DATE_FIELDS, 'dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeStyle'];

/**
 * date.toLocaleString(locale, { timeZone: China, ...options }) — or
 * toLocaleDateString when `dateOnly` — through a cached formatter, adding the
 * same default fields those methods add (a bare Intl.DateTimeFormat would show
 * the date only, where toLocaleString shows date and time).
 */
const inChina = (date, locale, options = {}, dateOnly = false) => {
  const opts = { timeZone: CHINA_TIMEZONE, ...options };
  // format() throws on an invalid date where toLocaleString returns "Invalid Date"
  if (Number.isNaN(date.getTime())) {
    return dateOnly ? date.toLocaleDateString(locale, opts) : date.toLocaleString(locale, opts);
  }
  const key = `${locale}|${dateOnly}|${JSON.stringify(options)}`;
  let formatter = chinaFormatters.get(key);
  if (!formatter) {
    const fields = dateOnly ? DATE_FIELDS : ANY_FIELDS;
    if (!fields.some((f) => opts[f] !== undefined)) {
      Object.assign(opts, { year: 'numeric', month: 'numeric', day: 'numeric' },
        dateOnly ? {} : { hour: 'numeric', minute: 'numeric', second: 'numeric' });
    }
    formatter = new Intl.DateTimeFormat(locale, opts);
    if (chinaFormatters.size < MAX_FORMATTERS) chinaFormatters.set(key, formatter);
  }
  return formatter.format(date);
};

const YMD_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit' };
// "MM/DD/YYYY" in China → "YYYY-MM-DD"
const chinaYmd = (date) => {
  const [month, day, year] = inChina(date, 'en-US', YMD_OPTIONS).split('/');
  return `${year}-${month}-${day}`;
};

/**
 * Parse UTC timestamps and return a Date object that represents the China timezone moment
 * @param {string} timestamp - The timestamp from database (UTC)
 * @returns {Date} - Date object (internally UTC, but we'll convert for display)
 */
// Reader, not writer: stored timestamps are UTC in two spellings (ISO-with-Z and
// SQLite's naive '2025-08-08 15:45:33'), and this appends the missing Z rather
// than converting. Backend counterpart: parseStoredTimestamp in
// backend/utils/chinaTime.js. Do not confuse either with clientTimestampToUTC,
// which converts naive LOCAL wall-clock arriving from a client.
export const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return new Date(0);

  // Handle date-only format (YYYY-MM-DD)
  // These should be treated as midnight in the local context, not UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
    // For date-only strings, append T00:00:00 to make it a proper timestamp
    // Then treat it as UTC
    return new Date(timestamp + 'T00:00:00Z');
  }

  // If timestamp already ends with 'Z', use as-is (already UTC)
  // If timestamp has 'T' but no timezone info, append 'Z' to treat as UTC
  let utcTimestamp;
  if (timestamp.endsWith('Z')) {
    utcTimestamp = timestamp; // Already UTC
  } else if (timestamp.includes('T')) {
    utcTimestamp = timestamp + 'Z'; // ISO format without timezone, treat as UTC
  } else {
    utcTimestamp = timestamp + 'Z'; // Fallback: treat as UTC
  }

  return new Date(utcTimestamp);
};

/**
 * Get current date/time in China timezone
 * @returns {Date} - Current date in China timezone
 */
export const getNowInChina = () => {
  return new Date(inChina(new Date(), 'en-US'));
};

/**
 * Get today's date string in China timezone (YYYY-MM-DD format)
 * @returns {string} - Today's date in China timezone
 */
export const getTodayInChina = () => chinaYmd(new Date());

/**
 * Get current hour in China timezone (0-23)
 * @returns {number} - Current hour in China timezone
 */
export const getChinaHour = () => {
  const chinaTimeStr = inChina(new Date(), 'en-US', { hour: 'numeric', hour12: false });
  return parseInt(chinaTimeStr, 10) % 24;
};

/**
 * Convert a UTC timestamp to China timezone for display
 * @param {string|Date} timestamp - UTC timestamp
 * @returns {Date} - Date object representing the China timezone moment
 */
export const toChinaTime = (timestamp) => {
  const date = typeof timestamp === 'string' ? parseUTCTimestamp(timestamp) : timestamp;
  return new Date(inChina(date, 'en-US'));
};

/**
 * Format timestamp in Chinese format: "8月24日 16:24"
 * ALWAYS displays in China timezone (UTC+8)
 * @param {string} timestamp - The timestamp to format (UTC)
 * @returns {string} - Formatted timestamp string in Chinese, China timezone
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';

  const date = parseUTCTimestamp(timestamp);
  return inChina(date, 'zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format timestamp with year in Chinese format: "2025年8月24日 16:24"
 * ALWAYS displays in China timezone (UTC+8)
 * @param {string} timestamp - The timestamp to format (UTC)
 * @returns {string} - Formatted timestamp string with year in Chinese, China timezone
 */
export const formatTimestampWithYear = (timestamp) => {
  if (!timestamp) return '';

  const date = parseUTCTimestamp(timestamp);
  return inChina(date, 'zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format date as YYYY-MM-DD in China timezone
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} - Date string in YYYY-MM-DD format, China timezone
 */
export const formatDateYMD = (timestamp) => {
  if (!timestamp) return '';

  const date = typeof timestamp === 'string' ? parseUTCTimestamp(timestamp) : timestamp;
  return chinaYmd(date);
};

/**
 * Convert UTC timestamp to YYYY-MM-DD in China timezone
 * Alias for formatDateYMD - matches backend naming convention
 * @param {string} utcTimestamp - UTC timestamp from API
 * @returns {string} - Date string in YYYY-MM-DD format, China timezone
 */
export const utcToChinaDate = formatDateYMD;

/**
 * Format date as localized string in China timezone
 * @param {string|Date} timestamp - The timestamp to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string in China timezone
 */
export const formatDateInChina = (timestamp, options = {}) => {
  if (!timestamp) return '';

  // Handle empty strings and whitespace
  if (typeof timestamp === 'string' && timestamp.trim() === '') return '';

  const date = typeof timestamp === 'string' ? parseUTCTimestamp(timestamp) : timestamp;

  // Check if date is valid
  if (isNaN(date.getTime())) return '';

  return inChina(date, 'zh-CN', options);
};

/**
 * Sort submissions by timestamp (newest first)
 * @param {Array} submissions - Array of submissions with created_at or submittedAt
 * @returns {Array} - Sorted submissions array
 */
export const sortSubmissionsByTime = (submissions, order = 'desc') => {
  return [...submissions].sort((a, b) => {
    const timeA = parseUTCTimestamp(a.created_at || a.submittedAt);
    const timeB = parseUTCTimestamp(b.created_at || b.submittedAt);
    
    return order === 'desc' ? timeB.getTime() - timeA.getTime() : timeA.getTime() - timeB.getTime();
  });
};

/**
 * Get the most recent submission from an array
 * @param {Array} submissions - Array of submissions
 * @returns {Object|null} - Most recent submission or null
 */
export const getMostRecentSubmission = (submissions) => {
  if (!submissions || submissions.length === 0) return null;

  return sortSubmissionsByTime(submissions, 'desc')[0];
};

/**
 * Format date in Chinese long format: "2025年1月24日"
 * ALWAYS displays in China timezone (UTC+8)
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} - Formatted Chinese date or fallback message
 */
export const formatChineseLongDate = (dateStr) => {
  if (!dateStr) return '无截止日期';
  const date = typeof dateStr === 'string' ? parseUTCTimestamp(dateStr) : dateStr;
  return inChina(date, 'zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }, true);
};

/**
 * Format month and year for calendar header: "January 2025"
 * ALWAYS displays in China timezone (UTC+8)
 * @param {Date} date - Date object
 * @returns {string} - Formatted month and year
 */
export const formatMonthYear = (date) => {
  return inChina(date, 'zh-CN', { month: 'long', year: 'numeric' });
};

/**
 * Get calendar dates for a month (including padding from previous/next months)
 * @param {Date} currentMonth - The month to get dates for
 * @returns {Date[]} - Array of Date objects for the calendar grid
 */
export const getCalendarDatesForMonth = (currentMonth) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - lastDay.getDay())); // End on Saturday

  const dates = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/**
 * Navigate to previous month
 * @param {Date} currentMonth - Current month
 * @returns {Date} - Previous month
 */
export const getPreviousMonth = (currentMonth) => {
  return new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
};

/**
 * Navigate to next month
 * @param {Date} currentMonth - Current month
 * @returns {Date} - Next month
 */
export const getNextMonth = (currentMonth) => {
  return new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
};

/**
 * Get relative due date display in Chinese
 * Uses China timezone for all comparisons
 * @param {string|Date} dueDate - Due date (UTC timestamp from API)
 * @returns {string} - Relative date display (e.g., "今天到期", "已过期", "到期 1/24/2025")
 */
export const getRelativeDueDateDisplay = (dueDate) => {
  if (!dueDate) return '无截止日期';

  // Convert due date to YYYY-MM-DD in China timezone
  const dueDateChina = formatDateYMD(dueDate);
  const todayChina = getTodayInChina();

  // Parse as simple date strings for comparison (YYYY-MM-DD format sorts correctly)
  if (dueDateChina === todayChina) {
    return '今天到期';
  } else if (dueDateChina < todayChina) {
    return '已过期';
  } else {
    // Calculate tomorrow in China timezone
    const now = new Date();
    const tomorrowDate = new Date(inChina(now, 'en-US'));
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowChina = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

    if (dueDateChina === tomorrowChina) {
      return '明天到期';
    } else {
      // Format the due date for display
      const displayDate = formatDateInChina(dueDate, { month: 'numeric', day: 'numeric' });
      return `到期 ${displayDate}`;
    }
  }
};

/**
 * Check if a date is today (in China timezone)
 * @param {string|Date} date - Date to check (UTC timestamp or Date object)
 * @returns {boolean}
 */
export const isDateToday = (date) => {
  const dateChina = formatDateYMD(date);
  const todayChina = getTodayInChina();
  return dateChina === todayChina;
};

/**
 * Check if a date is overdue (in China timezone)
 * @param {string|Date} date - Date to check (UTC timestamp or Date object)
 * @returns {boolean}
 */
export const isDateOverdue = (date) => {
  const dateChina = formatDateYMD(date);
  const todayChina = getTodayInChina();
  return dateChina < todayChina;
};

/**
 * Check if a date is in the future (after today in China timezone)
 * @param {string|Date} date - The date to check (UTC timestamp or Date object)
 * @returns {boolean} - true if date is after today in China timezone
 */
export const isFutureDate = (date) => {
  if (!date) return false;
  const dateChina = formatDateYMD(date);
  const todayChina = getTodayInChina();
  return dateChina > todayChina;
};

/**
 * Get date at midnight (zero time)
 * @param {Date} date - Date
 * @returns {Date} - Date at midnight
 */
export const getDateAtMidnight = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};